import React from 'react'

import { Card, CardContent, CardHeader } from '@mui/material'

import { CampaignCountries } from 'components/campaigns'
import { FormSelect } from 'components/input'
import { DescriptionListTable } from 'components/table'
import { withForm } from 'decorators/withForm'
import { useAssignUser } from 'hooks/useCampaign'
import { assignableUsersOptions } from 'models/campaign-model'
import { renderBudget, renderDate, renderProgress, renderTotalBudget } from 'utils/render/campaign-details'

const FORM_NAME = 'campaign-summary'

type CampaignDetailsSummaryProps = {
  campaignId: string
  data: Record<string, any>
  handleSubmit: (...args: any[]) => any
}

const CampaignDetailsSummary = ({ campaignId, data, handleSubmit }: CampaignDetailsSummaryProps) => {
  const assignUser = useAssignUser()

  const handleAssigneeChange = (assigneeId: any) => assignUser.mutate({ campaignId, assigneeId: assigneeId || null })

  const config = [
    {
      title: 'Date',
      render: renderDate,
    },
    {
      title: 'Status',
      field: 'status',
    },
    {
      title: 'Countries',
      render: CampaignCountries,
    },
    {
      title: 'CTR',
      render: renderProgress('ctr')('matchedClicks', 'matchedViews', 'percent', true),
    },
    {
      title: 'Plan',
      render: renderProgress('plan')('matchedViews', 'totalViews'),
    },
    {
      title: 'Completion daily',
      render: renderProgress('completionDaily', true)('matchedViewsPerDay', 'totalViewsPerDay'),
    },
    {
      title: 'Target CTR',
      render: renderProgress('targetCtr')('companiesWithClicks', 'companiesWithViews'),
    },
    {
      title: 'Estimated Total Budget',
      render: renderTotalBudget(),
    },
    {
      title: 'Daily budget',
      render: renderBudget('dailyBudget'),
    },
    {
      title: 'CPM',
      render: renderBudget('cpm'),
    },
    {
      title: 'Currency',
      field: 'currency',
    },
    {
      title: 'Customer cost',
      render: renderBudget('customerCost'),
    },
    {
      title: 'Goal',
      field: 'goal',
    },
    {
      title: 'Paid',
      render: ({ paid }: any) => paid.toString(),
    },
    {
      title: 'UUID',
      field: 'uuid',
    },
    {
      title: 'Assignee',
      render: () => (
        <FormSelect name="assignedUserId" onChange={handleAssigneeChange} options={assignableUsersOptions} />
      ),
    },
  ]

  return (
    <form onSubmit={handleSubmit(() => {})}>
      <Card>
        <CardHeader title="Summary" />
        <CardContent>
          <DescriptionListTable config={config} data={data} titleCellStyle={{ width: 100 }} />
        </CardContent>
      </Card>
    </form>
  )
}

const CampaignDetailsSummaryForm = withForm({
  form: FORM_NAME,
})(CampaignDetailsSummary)

// initialValues (the assignee) is now seeded from the `data` (details) prop instead of the connect-injected campaign slice.
const CampaignDetailsSummaryWithInitialValues = (props: any) => (
  <CampaignDetailsSummaryForm {...props} initialValues={{ assignedUserId: props.data?.assignedUserId }} />
)

export default CampaignDetailsSummaryWithInitialValues
